import { formatDate, formatPrice } from '@/helpers';
import { DayjsFormat } from '@/helpers/date-time';
import { MealPeriodEnum, MenuTemplateStateEnum, ProductEnum } from '@/api';

import {
  INCLUDED_RULE_NOTE,
  MEAL_PERIOD_LABEL,
  MENU_ITEM_PLACEHOLDER_TOKEN,
  MENU_LEVEL_PRICING_TYPE,
  MENU_STATE_LABEL,
  NO_IMAGE_PLACEHOLDER_SRC,
  OPTIONAL_LABEL,
  PER_PERSON_SUFFIX,
  PRODUCT_TYPE_FILTER_OPTIONS,
  PRODUCT_TYPE_LABEL,
} from './constants';
import type {
  MenuCategory,
  MenuCategorySection,
  MenuItem,
  MenuOption,
  MenuOptionGroup,
  MenuRestaurant,
  MenuTemplateDetail,
  MenuTemplateFilters,
  MenuTemplateSummary,
} from './types';

export const formatMenuDate = (date: string): string =>
  formatDate(date, DayjsFormat.SHORT_DATE_WITH_YEAR);

export const resolveMenuItemImage = (url: string): string =>
  url?.includes(MENU_ITEM_PLACEHOLDER_TOKEN) ? NO_IMAGE_PLACEHOLDER_SRC : url;

export const productTypeLabel = (productType: ProductEnum): string =>
  PRODUCT_TYPE_LABEL[productType] ?? productType;

export const mealPeriodLabel = (mealPeriod: MealPeriodEnum): string =>
  MEAL_PERIOD_LABEL[mealPeriod];

export const stateLabel = (state: MenuTemplateStateEnum): string => MENU_STATE_LABEL[state];

export const mealPeriodsLabel = (mealPeriods: MealPeriodEnum[]): string =>
  mealPeriods.map(mealPeriodLabel).join(', ');

export const menuLevelPriceLabel = (template: MenuTemplateDetail): string | null => {
  if (!('pricingType' in template) || template.pricingType !== MENU_LEVEL_PRICING_TYPE) {
    return null;
  }
  if (!template.menuLevelPriceCents) return null;

  return `${formatPrice(template.menuLevelPriceCents)} ${PER_PERSON_SUFFIX}`;
};

export const isOptionGroupRequired = ({ minSelections }: MenuOptionGroup): boolean =>
  minSelections > 0;

export const selectionRuleLabel = ({ minSelections, maxSelections }: MenuOptionGroup): string => {
  if (minSelections === 0 && maxSelections === 0) return OPTIONAL_LABEL;
  // maxSelections of 0 with a minimum means there is no upper limit.
  if (maxSelections === 0) return `Choose at least ${minSelections}`;
  if (minSelections === maxSelections) return `Choose only ${minSelections}`;
  if (minSelections === 0) return `Choose up to ${maxSelections} • ${OPTIONAL_LABEL}`;
  return `Choose ${minSelections}–${maxSelections}`;
};

export const includedAllowanceLabel = (group: MenuOptionGroup): string | null =>
  group.includedSelections > 0 ? `${group.includedSelections} ${INCLUDED_RULE_NOTE}` : null;

export const optionPriceLabel = (option: MenuOption): string | null => {
  const price = formatPrice(option.preTaxPriceCents);
  return price ? `+${price}` : null;
};

export const menuItemPriceLabel = (item: MenuItem): string | null => {
  const price = formatPrice(item.preTaxPriceCents);
  if (price) return price;
  if (!('perPersonUpchargeCents' in item) || !item.perPersonUpchargeCents) return null;

  return `+${formatPrice(item.perPersonUpchargeCents)} ${PER_PERSON_SUFFIX}`;
};

export const filterTemplates = (
  templates: MenuTemplateSummary[],
  filters: MenuTemplateFilters
): MenuTemplateSummary[] =>
  templates.filter((template) => {
    const productMatch = filters.productTypes.includes(template.productType);
    const mealPeriodMatch =
      filters.mealPeriods.length === 0 ||
      template.mealPeriods.some((mealPeriod) => filters.mealPeriods.includes(mealPeriod));
    const stateMatch = filters.states.length === 0 || filters.states.includes(template.state);

    return productMatch && mealPeriodMatch && stateMatch;
  });

export const hasActiveFilters = (filters: MenuTemplateFilters): boolean =>
  filters.productTypes.length !== PRODUCT_TYPE_FILTER_OPTIONS.length ||
  filters.mealPeriods.length > 0 ||
  filters.states.length > 0;

export const resolveSelectedVendorId = (
  restaurants: MenuRestaurant[],
  currentVendorId: string | null
): string | null => {
  if (restaurants.length === 0) return null;
  const stillValid = restaurants.some((restaurant) => restaurant.id === currentVendorId);
  return stillValid ? currentVendorId : restaurants[0].id;
};

export const resolveSelectedTemplateId = (
  templates: MenuTemplateSummary[],
  currentTemplateId: string | null
): string | null => {
  if (templates.length === 0) return null;
  const stillVisible = templates.some((template) => template.id === currentTemplateId);
  return stillVisible ? currentTemplateId : templates[0].id;
};

export const groupItemsByCategory = (
  items: MenuItem[],
  categories: MenuCategory[]
): MenuCategorySection[] => {
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const sections: MenuCategorySection[] = [];
  const sectionIndexByKey = new Map<string, number>();

  items.forEach((item) => {
    const categoryId = item.category?.id ?? null;
    const key = categoryId ?? '__uncategorized__';
    const existingIndex = sectionIndexByKey.get(key);

    if (existingIndex === undefined) {
      sectionIndexByKey.set(key, sections.length);
      sections.push({
        category: categoryId ? categoryById.get(categoryId) ?? null : null,
        items: [item],
      });
      return;
    }

    sections[existingIndex].items.push(item);
  });

  return sections;
};
